import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import '../lib/axiosCache';   // installs in-flight dedupe + 5s GET cache globally
import { installAuthHeader, setToken, clearToken, getToken } from '../lib/authToken';

const AuthContext = createContext(null);
const API = process.env.REACT_APP_BACKEND_URL + '/api';

axios.defaults.withCredentials = true;
// iter187 — iPhone third-party-cookie workaround. Must run before any
// `/api/*` call so the Authorization header rides along.
installAuthHeader();

// iter189 — re-login storm fix. Production users on desktop Chrome were
// getting kicked back to /login every 1-2 minutes. Root cause: the previous
// AuthContext logged the user out on ANY error from /api/auth/me — a network
// blip, gateway 502, or even a CORS preflight failure would wipe the token
// and the session. Now we only clear auth on actual 401 (real auth failure).
async function fetchMeWithRetry(maxAttempts = 2) {
  let lastErr;
  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    try {
      // bypassCache so the 5s GET cache doesn't serve a stale (potentially
      // tokenless) response when we're explicitly refreshing the session.
      const res = await axios.get(`${API}/auth/me`, { bypassCache: true });
      return { ok: true, data: res.data };
    } catch (err) {
      lastErr = err;
      const status = err.response?.status;
      if (status === 401) return { ok: false, status: 401, err };
      // Transient (network / 5xx / no-response) — back off and retry once
      if (attempt < maxAttempts) {
        await new Promise(r => setTimeout(r, 500));
      }
    }
  }
  return { ok: false, status: lastErr?.response?.status || 0, err: lastErr, transient: true };
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null=loading, false=not auth, obj=auth

  useEffect(() => {
    let mounted = true;
    let interval;

    const loadMe = async () => {
      const result = await fetchMeWithRetry();
      if (!mounted) return;
      if (result.ok) {
        if (result.data?.token) setToken(result.data.token);
        setUser(result.data);
      } else if (result.status === 401) {
        // Real auth failure — clear and force re-login
        clearToken();
        setUser(false);
      } else {
        // Transient failure — DO NOT log out. If we already had a user,
        // keep them logged in. If first load and we had a token in
        // localStorage, optimistically assume the session is still valid
        // (the next API call will get a real 401 if it isn't).
        if (user === null) {
          // First mount, never resolved — fall back to "not auth" only if
          // there's no token at all, otherwise keep loading state.
          if (!getToken()) setUser(false);
        }
        // If user was already set, keep them logged in.
      }
    };

    loadMe();
    // Periodic sliding refresh — every 5 minutes the frontend hits /me to
    // refresh the cookie & rotate the JWT if backend deems it close to
    // expiry. Keeps long sessions alive without surprise logouts.
    interval = setInterval(loadMe, 5 * 60 * 1000);

    return () => { mounted = false; if (interval) clearInterval(interval); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const login = async (email, password) => {
    const res = await axios.post(`${API}/auth/login`, { email, password });
    if (res.data?.token) setToken(res.data.token);
    setUser(res.data);
    return res.data;
  };

  const logout = async () => {
    try { await axios.post(`${API}/auth/logout`); } catch { /* ignore */ }
    clearToken();
    setUser(false);
  };

  return (
    <AuthContext.Provider value={{ user, setUser, login, logout, API }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
export default AuthContext;
